import { useQuery } from '@tanstack/react-query';
import { propertiesApi } from 'API/properties-api/properties-api';
import { ComparisonDates } from 'types/properties-data';
import { DEFAULT_QUERY_STALE_TIME } from 'queries/constant';
import { accountPropertiesQueryKeys } from 'hooks/queries/properties/property-keys';

type GetAccountPropertiesQueryProps = {
    accountPropertiesTagId?: string;
    dates?: ComparisonDates;
    enabled?: boolean;
};

const ITEMS_LIMIT = 500;

const getAccountPaginatedProperties = async (
    accountPropertiesTagId: string | undefined,
    dates: ComparisonDates,
    signal: AbortSignal | undefined,
) => {
    let currentPage = 0;
    const baseResponse = await propertiesApi.getAccountPropertiesPage({
        limit: ITEMS_LIMIT,
        dates,
        accountPropertiesTagId,
        signal,
        skip: currentPage * ITEMS_LIMIT,
    });
    currentPage++;

    const { total = 0 } = baseResponse;
    let allData = baseResponse;

    const promises = [];

    while (currentPage * ITEMS_LIMIT < total) {
        promises.push(
            propertiesApi.getAccountPropertiesPage({
                limit: ITEMS_LIMIT,
                dates,
                accountPropertiesTagId,
                signal,
                skip: currentPage * ITEMS_LIMIT,
            }),
        );

        currentPage++;
    }

    const res = (await Promise.all(promises)) ?? [];

    res.forEach((response) => {
        const { entities, lists } = response;
        allData.entities = [...allData.entities, ...entities];
        allData.lists = { ...allData.lists, ...lists };
    });

    return allData;
};

export const useGetAccountPropertiesQuery = ({
    enabled = true,
    accountPropertiesTagId,
    dates,
}: GetAccountPropertiesQueryProps = {}) => {
    return useQuery({
        queryKey: accountPropertiesQueryKeys.filters({ tagId: accountPropertiesTagId, dates }),
        keepPreviousData: true,
        enabled: enabled && !!dates && !!accountPropertiesTagId,
        staleTime: DEFAULT_QUERY_STALE_TIME,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        queryFn: async ({ signal }) =>
            getAccountPaginatedProperties(accountPropertiesTagId, dates!, signal),
    });
};
